import {
  Autocomplete,
  Box,
  Button,
  Container,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import { t } from 'i18next'
import Joi from 'joi'
import { useEffect, useState } from 'react'

import useFieldValidation from '../../../helpers/fieldValidation.js'
import CloseDialogButton from '../../Shared/Dialogs/CloseDialogButton.jsx'
import { useDevices } from '../Contexts/DevicesContext.jsx'

const BRAND_DEFAULT_STATE = {
  name: null,
  nameEncoded: null
}
const MODEL_DEFAULT_STATE = {
  name: null,
  nameEncoded: null,
  group: null,
  type: null,
  sourceType: null,
  missingCommunicationTimeoutSeconds: null,
  netServerProfileId: null,
  requiredDownlinksAck: false
}

const schemaBrand = Joi.object({
  name: Joi.string().empty([null, '']).required(),
  nameEncoded: Joi.string().empty([null, '']).required()
})

const schemaModel = Joi.object({
  name: Joi.string().empty([null, '']).required(),
  nameEncoded: Joi.string().empty([null, '']).required(),
  group: Joi.object().empty([null, '']).required(),
  type: Joi.object().empty([null, '']).required(),
  sourceType: Joi.string().empty([null, '']).required(),
  missingCommunicationTimeoutSeconds: [
    Joi.number().min(1).empty([null]).optional(),
    Joi.allow(null)
  ],
  netServerProfileId: Joi.number().min(1).empty([null]).required(),
  requiredDownlinksAck: Joi.boolean().default(false)
})

const filter = createFilterOptions()

const AddBrandModel = ({ isOpen, setIsOpen }) => {
  const { useFetchBrands, useFetchDeviceGroups, useFetchDeviceTypes, useCreateModel } = useDevices()

  const [, setIsBrandsLoading] = useState(false)
  const [deviceGroups, setDeviceGroups] = useState([])
  const [isDeviceGroupsLoading, setIsDeviceGroupsLoading] = useState(false)
  const [isDeviceGroupsError, setIsDeviceGroupsError] = useState(false)
  const [deviceTypes, setDeviceTypes] = useState([])
  const [isDeviceTypesLoading, setIsDeviceTypesLoading] = useState(false)
  const [isDeviceTypesError, setIsDeviceTypesError] = useState(false)
  const [brands, setBrands] = useState([])
  const [formBrand, setFormBrand] = useState(BRAND_DEFAULT_STATE)
  const [formModel, setFormModel] = useState(MODEL_DEFAULT_STATE)
  const [brandValidation, setBrandValidation] = useState({})
  const [modelValidation, setModelValidation] = useState({})
  const [openAddBrandDialog, setOpenAddBrandDialog] = useState(false)
  const [addBrandDialogValue, setAddBrandDialogValue] = useState({
    name: '',
    nameEncoded: ''
  })

  const handleChangeBrand = (event) => {
    const {
      target: { name, value }
    } = event

    setFormBrand({
      ...formBrand,
      [name]: value
    })
  }

  const handleChangeModel = (event) => {
    const {
      target: { name, value }
    } = event

    setFormModel({
      ...formModel,
      [name]: value
    })
  }

  const handleChangeNumber = (event) => {
    const {
      target: { name, value }
    } = event

    setFormModel({
      ...formModel,
      [name]: parseInt(value)
    })
  }

  const handleChangeBoolean = (event) => {
    const {
      target: { name, checked }
    } = event

    setFormModel({
      ...formModel,
      [name]: checked
    })
  }

  const handleChangeGropus = (_, value) => {
    setFormModel({
      ...formModel,
      group: value
    })
  }

  const handleChangeTypes = (_, value) => {
    setFormModel({
      ...formModel,
      type: value
    })
  }

  const handleCloseAddBrand = () => {
    setAddBrandDialogValue({
      name: '',
      nameEncoded: ''
    })
    setOpenAddBrandDialog(false)
  }

  const handleSubmit = (event) => {
    event.preventDefault()

    setFormBrand({
      name: addBrandDialogValue.name,
      nameEncoded: addBrandDialogValue.nameEncoded
    })

    handleCloseAddBrand()
  }

  const handleClose = () => {
    setIsOpen(false)
    setBrandValidation({})
    setModelValidation({})
    setFormBrand(BRAND_DEFAULT_STATE)
    setFormModel(MODEL_DEFAULT_STATE)
  }

  const handleConfirm = () => {
    const brandValidation = useFieldValidation(formBrand, schemaBrand)
    const modelValidation = useFieldValidation(formModel, schemaModel)
    setBrandValidation(brandValidation)
    setModelValidation(modelValidation)

    if (!brandValidation.isValid || !modelValidation.isValid) {
      return
    }

    const payload = {
      name: formModel.name,
      nameEncoded: formModel.nameEncoded,
      brandName: formBrand.name,
      brandNameEncoded: formBrand.nameEncoded,
      groupId: formModel.group.id,
      typeId: formModel.type.id,
      sourceType: formModel.sourceType,
      netServerProfileId: formModel.netServerProfileId,
      missingCommunicationTimeoutSeconds: formModel.missingCommunicationTimeoutSeconds,
      requiredDownlinksAck: formModel.requiredDownlinksAck
    }

    useCreateModel(payload)
      .then(() => {
        setIsOpen(false)
        setFormBrand(BRAND_DEFAULT_STATE)
        setFormModel(MODEL_DEFAULT_STATE)
      })
      .catch((err) => openErrorAlert({ keys: err.data.keys }))
  }

  useEffect(() => {
    setIsDeviceGroupsLoading(true)
    setIsDeviceTypesLoading(true)
    setIsBrandsLoading(true)

    useFetchDeviceGroups()
      .then((res) => setDeviceGroups(res.data))
      .catch(() => setIsDeviceGroupsError(true))
      .finally(() => setIsDeviceGroupsLoading(false))

    useFetchDeviceTypes()
      .then((res) => setDeviceTypes(res.data))
      .catch(() => setIsDeviceTypesError(true))
      .finally(() => setIsDeviceTypesLoading(false))

    useFetchBrands()
      .then((res) => setBrands(res.data))
      .catch(() => setIsBrandsError(true))
      .finally(() => setIsBrandsLoading(false))
  }, [])

  return (
    <>
      <Dialog
        maxWidth={'lg'}
        open={isOpen}
        scroll="body"
        onClose={handleClose}
        aria-labelledby="add-brand-modal-title"
        aria-describedby="add-brand-modal-description"
      >
        <DialogTitle
          display="flex"
          justifyContent="center"
          variant="h4"
        >
          <Box mt={7}>{t('devices.addBrandModel.title')}</Box>
        </DialogTitle>

        <CloseDialogButton onClose={handleClose} />

        <DialogContent>
          <Container
            maxWidth="lg"
            sx={{ pt: 3 }}
          >
            <Typography variant="h6">
              {t('devices.addBrandModel.brand.title')}
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              mt={2}
            >
              <Autocomplete
                value={formBrand}
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                    setTimeout(() => {
                      setOpenAddBrandDialog(true)
                      setAddBrandDialogValue({
                        name: newValue,
                        nameEncoded: ''
                      })
                    })
                  } else if (newValue && newValue.inputValue) {
                    setOpenAddBrandDialog(true)
                    setAddBrandDialogValue({
                      name: newValue.inputValue,
                      nameEncoded: ''
                    })
                  } else {
                    setFormBrand(newValue || BRAND_DEFAULT_STATE)
                  }
                }}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params)

                  if (params.inputValue !== '') {
                    filtered.push({
                      inputValue: params.inputValue,
                      name: `Aggiungi "${params.inputValue}"`
                    })
                  }

                  return filtered
                }}
                id="brand-autocomplete"
                options={brands}
                getOptionLabel={(option) => {
                  // for example value selected with enter, right from the input
                  if (typeof option === 'string') {
                    return option || ''
                  }
                  if (option.inputValue) {
                    return option.inputValue || ''
                  }
                  return option.name || ''
                }}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props
                  return (
                    <li key={key} {...optionProps}>
                      {option.name || ''}
                    </li>
                  )
                }}
                freeSolo
                fullWidth
                renderInput={(params) => <TextField {...params} label="Nome" />}
                error={brandValidation.messages?.name?.length > 0}
                helperText={brandValidation.messages?.name?.join(', ')}
              />
              <TextField
                id="brand-name-encoded"
                name="nameEncoded"
                fullWidth
                value={formBrand?.nameEncoded || ''}
                label={t('devices.addBrandModel.brand.nameEncoded')}
                required
                onChange={handleChangeBrand}
                error={brandValidation.messages?.nameEncoded?.length > 0}
                helperText={brandValidation.messages?.nameEncoded?.join(', ')}
              />
            </Stack>
            <Divider
              sx={{
                mt: 5,
                mb: 5
              }}
            />
            <Typography variant="h6">
              {t('devices.addBrandModel.model.title')}
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              mt={2}
            >
              <TextField
                id="Model-name"
                name="name"
                value={formModel?.name || ''}
                fullWidth
                label={t('devices.addBrandModel.model.name')}
                required
                onChange={handleChangeModel}
                error={modelValidation.messages?.name?.length > 0}
                helperText={modelValidation.messages?.name?.join(', ')}
              />
              <TextField
                id="Model-nameEncoded"
                name="nameEncoded"
                value={formModel?.nameEncoded || ''}
                fullWidth
                label={t('devices.addBrandModel.model.nameEncoded')}
                required
                onChange={handleChangeModel}
                error={modelValidation.messages?.nameEncoded?.length > 0}
                helperText={modelValidation.messages?.nameEncoded?.join(', ')}
              />
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              mt={2}
            >
              <Autocomplete
                id="Model-group"
                options={deviceGroups}
                getOptionLabel={(option) => option.name}
                onChange={handleChangeGropus}
                loading={isDeviceGroupsLoading}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                disabled={isDeviceGroupsError}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('devices.addBrandModel.model.group')}
                    required
                    error={modelValidation.messages?.group?.length > 0}
                    helperText={modelValidation.messages?.group?.join(', ')}
                  />
                )}
                fullWidth
              />

              <Autocomplete
                id="Model-type"
                options={deviceTypes}
                getOptionLabel={(option) => option.name}
                onChange={handleChangeTypes}
                loading={isDeviceTypesLoading}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                disabled={isDeviceTypesError}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('devices.addBrandModel.model.type')}
                    required
                    error={modelValidation.messages?.type?.length > 0}
                    helperText={modelValidation.messages?.type?.join(', ')}
                  />
                )}
                fullWidth
              />
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              mt={2}
            >
              <TextField
                id="Model-source_type"
                name="sourceType"
                value={formModel?.sourceType || ''}
                fullWidth
                label={t('devices.addBrandModel.model.sourceType')}
                required
                onChange={handleChangeModel}
                error={modelValidation.messages?.sourceType?.length > 0}
                helperText={modelValidation.messages?.sourceType?.join(', ')}
                select
              >
                <MenuItem value={'lorawan'}>lorawan</MenuItem>
                <MenuItem value={'http'}>http</MenuItem>
                <MenuItem value={'wmbus'}>wmbus</MenuItem>
              </TextField>

              <TextField
                id="Model-missing_comunication_timeout"
                name="missingCommunicationTimeoutSeconds"
                type="number"
                value={formModel?.missingCommunicationTimeoutSeconds || ''}
                fullWidth
                label={t(
                  'devices.addBrandModel.model.missingCommunicationTimeoutSeconds'
                )}
                slotProps={{ min: 1 }}
                onChange={handleChangeNumber}
                error={
                  modelValidation.messages?.missingCommunicationTimeoutSeconds?.length >
                  0
                }
                helperText={modelValidation.messages?.missingCommunicationTimeoutSeconds?.join(
                  ', '
                )}
              />
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              mt={2}
            >
              <TextField
                id="Model-netServerProfileId"
                name="netServerProfileId"
                type="number"
                value={formModel?.netServerProfileId || ''}
                fullWidth
                label={t('devices.addBrandModel.model.netServerProfileId')}
                required
                slotProps={{ min: 1 }}
                onChange={handleChangeNumber}
                error={modelValidation.messages?.netServerProfileId?.length > 0}
                helperText={modelValidation.messages?.netServerProfileId?.join(
                  ', '
                )}
              />

              <FormControl fullWidth>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formModel.requiredDownlinksAck}
                      value={formModel.requiredDownlinksAck}
                      id="Model-requiredDownlinksAck"
                      name="requiredDownlinksAck"
                      onChange={handleChangeBoolean}
                    />
                  }
                  label={
                    <Typography
                      variant="caption"
                      display="block"
                      gutterBottom
                      sx={{ color: 'rgba(0, 0, 0, 0.6);' }}
                    >
                      {t('devices.addBrandModel.model.requiredDownlinksAck')}
                    </Typography>
                  }
                  labelPlacement="top"
                />
              </FormControl>
            </Stack>
          </Container>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Button
            onClick={handleClose}
            variant="standard"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={!formBrand.name || !formModel.name}
            onClick={() => handleConfirm()}
          >
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAddBrandDialog} onClose={handleCloseAddBrand}>
        <form onSubmit={handleSubmit}>
          <DialogTitle>Aggiungi nuova marca</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Se la marca non è presente nell&apos;elenco, puoi aggiungerla qui.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              value={addBrandDialogValue.name}
              onChange={(event) =>
                setAddBrandDialogValue({
                  ...addBrandDialogValue,
                  name: event.target.value
                })
              }
              label="name"
              type="text"
              variant="standard"
            />
            <TextField
              margin="dense"
              id="nameEncoded"
              value={addBrandDialogValue.nameEncoded}
              onChange={(event) =>
                setAddBrandDialogValue({
                  ...addBrandDialogValue,
                  nameEncoded: event.target.value
                })
              }
              label="name_encoded"
              type="text"
              variant="standard"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddBrand}>Annulla</Button>
            <Button type="submit">Aggiungi</Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}

export default AddBrandModel
